import { useCallback, useEffect, useMemo, useState } from 'react';
import { fromCents, remainingAmount, splitEvenly, sumLines, toCents, validateLines } from './allocationMath';

let keyCounter = 0;
const nextKey = () => `draft-${++keyCounter}`;

/*
  Turns the lines of a card payload into editable draft rows. A row is editable when it is pending and belongs to
  the current user (admins may edit every pending line).
*/
export function buildDraftLines(card, { currentUserId, isAdmin }) {
  return (card?.lines || []).map((line) => ({
    _key: `line-${line.id}`,
    id: line.id,
    Bedrag: Number(line.Bedrag) || 0,
    FactuurTypeID: line.FactuurTypeID ?? null,
    ProjectID: line.ProjectID ?? null,
    projectNumber: line.projectNumber || null,
    projectDescription: line.projectDescription || null,
    BusinessUnitID: line.BusinessUnitID ?? null,
    Notitie: line.Notitie || '',
    approverId: line.approverId ?? null,
    approverAfkorting: line.approverAfkorting || null,
    originalApproverId: line.approverId ?? null,
    Status: line.Status || 'PENDING',
    RejectReason: line.RejectReason || null,
    editable: line.Status === 'PENDING' && (isAdmin || Number(line.approverId) === Number(currentUserId))
  }));
}

export function useAllocationDraft(card, { currentUserId, isAdmin, requireCostType = true } = {}) {
  const [lines, setLines] = useState(() => buildDraftLines(card, { currentUserId, isAdmin }));
  const [dirty, setDirty] = useState(false);
  const cardId = card?.document?.[card?.docType === 'creditnote' ? 'CreditnotaINvolgnummer' : 'faktuurinvolgnummer'];
  const stamp = card?.document?.ApprovalStatusUpdatedAt;
  const lineStamp = (card?.lines || []).map((l) => `${l.id}:${l.Status}:${l.Bedrag}:${l.approverId}`).join('|');

  useEffect(() => {
    setLines(buildDraftLines(card, { currentUserId, isAdmin }));
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId, stamp, lineStamp, currentUserId, isAdmin]);

  const target = card?.allocation?.basis ?? null;

  const mutate = useCallback((updater) => {
    setLines((previous) => updater(previous));
    setDirty(true);
  }, []);

  const update = useCallback((index, patch) => mutate((previous) => previous.map((row, i) => (i === index ? { ...row, ...patch } : row))), [mutate]);
  const remove = useCallback((index) => mutate((previous) => previous.filter((_, i) => i !== index)), [mutate]);
  const add = useCallback(
    (partial = {}) =>
      mutate((previous) => {
        const template = previous.find((row) => row.editable) || previous[previous.length - 1] || {};
        return [
          ...previous,
          {
            _key: nextKey(),
            id: null,
            Bedrag: 0,
            FactuurTypeID: template.FactuurTypeID ?? null,
            ProjectID: null,
            projectNumber: null,
            BusinessUnitID: template.BusinessUnitID ?? null,
            Notitie: '',
            approverId: null,
            approverAfkorting: null,
            originalApproverId: null,
            Status: 'PENDING',
            editable: true,
            ...partial
          }
        ];
      }),
    [mutate]
  );

  const splitRemaining = useCallback(() => {
    const remaining = remainingAmount(lines, target);
    add({ Bedrag: remaining > 0 ? remaining : 0 });
  }, [add, lines, target]);

  const splitEvenlyOverEditable = useCallback(() => {
    if (target === null) return;
    mutate((previous) => {
      const editableIndexes = previous.map((row, i) => (row.editable ? i : -1)).filter((i) => i >= 0);
      if (!editableIndexes.length) return previous;
      const fixed = previous.filter((row) => !row.editable).reduce((sum, row) => sum + toCents(row.Bedrag), 0);
      const parts = splitEvenly(fromCents(toCents(target) - fixed), editableIndexes.length);
      return previous.map((row, i) => (row.editable ? { ...row, Bedrag: parts[editableIndexes.indexOf(i)] } : row));
    });
  }, [mutate, target]);

  const fillToBalance = useCallback(
    (index) =>
      mutate((previous) => {
        const others = previous.filter((_, i) => i !== index);
        const remaining = remainingAmount(others, target);
        return previous.map((row, i) => (i === index ? { ...row, Bedrag: remaining > 0 ? remaining : 0 } : row));
      }),
    [mutate, target]
  );

  const applyProject = useCallback(
    (projectId, projectNumber, { onlyEmpty = true } = {}) =>
      mutate((previous) => previous.map((row) => (row.editable && (!onlyEmpty || !row.ProjectID) ? { ...row, ProjectID: projectId, projectNumber } : row))),
    [mutate]
  );

  const reset = useCallback(() => {
    setLines(buildDraftLines(card, { currentUserId, isAdmin }));
    setDirty(false);
  }, [card, currentUserId, isAdmin]);

  const validation = useMemo(() => validateLines(lines, target, { requireCostType }), [lines, target, requireCostType]);
  const editableValidation = useMemo(() => {
    const errors = {};
    lines.forEach((row, index) => {
      if (row.editable && validation.errors[index]) errors[index] = validation.errors[index];
    });
    return errors;
  }, [lines, validation]);

  const toPayload = useCallback(
    () =>
      lines
        .filter((row) => row.editable)
        .map((row) => ({
          id: row.id,
          Bedrag: Number(String(row.Bedrag).replace(',', '.')) || 0,
          FactuurTypeID: row.FactuurTypeID || null,
          ProjectID: row.ProjectID || null,
          BusinessUnitID: row.BusinessUnitID || null,
          Notitie: row.Notitie || null,
          ...(row.approverId && row.approverId !== row.originalApproverId ? { approverId: row.approverId } : {})
        })),
    [lines]
  );

  return {
    lines,
    setLines,
    dirty,
    update,
    add,
    remove,
    splitRemaining,
    splitEvenly: splitEvenlyOverEditable,
    fillToBalance,
    applyProject,
    reset,
    target,
    allocated: sumLines(lines),
    remaining: validation.remaining,
    isBalanced: validation.isBalanced,
    rowErrors: editableValidation,
    hasRowErrors: Object.keys(editableValidation).length > 0,
    toPayload
  };
}
